import {
  Button,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useCustomDashboardSettings } from "../hooks/use-custom-dashboard-settings";

import type { EditableCustomDashboardConfig } from "../../luzmo/custom-dashboard-config";
import type { FC } from "react";

export const AccountCustomDashboardSettings: FC<{
  initialValue: EditableCustomDashboardConfig;
  onSave: (value: EditableCustomDashboardConfig) => Promise<void | undefined>;
  onDelete?: () => Promise<void | undefined>;
}> = ({ initialValue, onSave, onDelete }) => {
  const {
    enabled,
    setEnabled,
    slug,
    setSlug,
    tabName,
    setTabName,
    attemptedSave,
    saving,
    slugError,
    tabNameError,
    save,
  } = useCustomDashboardSettings(initialValue, onSave);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {initialValue.id ? initialValue.tabName : "New dashboard"}
      </Typography>
      <Stack spacing={2}>
        <FormControlLabel
          control={
            <Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
          }
          label="Enable custom Luzmo dashboard"
        />
        <TextField
          label="Luzmo dashboard slug"
          value={slug}
          onChange={(event) => setSlug(event.target.value)}
          error={attemptedSave && slugError}
          helperText={
            attemptedSave && slugError
              ? "Enter a valid dashboard slug"
              : "The dashboard's slug in the ChangeEngine Luzmo integration"
          }
          fullWidth
        />
        <TextField
          label="Tab name"
          value={tabName}
          onChange={(event) => setTabName(event.target.value)}
          error={attemptedSave && tabNameError}
          helperText={attemptedSave && tabNameError ? "Enter a tab name" : undefined}
          fullWidth
        />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          {onDelete && (
            <Button
              color="error"
              onClick={() => void onDelete()}
              disabled={saving}
              data-analytics-id="ce-admin-delete-custom-luzmo-dashboard"
            >
              Delete
            </Button>
          )}
          <Button
            data-analytics-id="ce-admin-save-custom-luzmo-dashboard"
            variant="contained"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};
